/**
 * Provides the active `WorkforceGateway` (and, in demo mode, the `DemoApi`) to
 * the tree.
 *
 * When demo mode is off it resolves synchronously to the production `ApiGateway`
 * (no demo api). When on, it dynamically imports the demo module (code-split)
 * and mounts the `DemoGateway`, gating the app on a brief splash so there is no
 * data flash or cache-invalidation dance.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Splash } from '../components/Splash.js';
import { DEMO_ENABLED } from '../demo/flag.js';
import { makeGateway } from './index.js';
import type { WorkforceGateway } from './types.js';
import type { DemoApi } from './demo-api.js';

interface GatewayBundle {
  gateway: WorkforceGateway;
  demo: DemoApi | null;
}

const GatewayContext = createContext<GatewayBundle | null>(null);

export function GatewayProvider({ children }: { children: ReactNode }) {
  const [bundle, setBundle] = useState<GatewayBundle | null>(() =>
    DEMO_ENABLED ? null : { gateway: makeGateway(), demo: null },
  );

  useEffect(() => {
    if (bundle) return;
    let alive = true;
    import('../demo/index.js')
      .then((m) => m.loadDemo())
      .then((loaded) => {
        if (alive) setBundle(loaded);
      });
    return () => {
      alive = false;
    };
  }, [bundle]);

  if (!bundle) return <Splash label="Loading workspace…" />;
  return <GatewayContext.Provider value={bundle}>{children}</GatewayContext.Provider>;
}

function useBundle(): GatewayBundle {
  const b = useContext(GatewayContext);
  if (!b) throw new Error('useGateway must be used within a GatewayProvider');
  return b;
}

export function useGateway(): WorkforceGateway {
  return useBundle().gateway;
}

/** The demo controls, or null when demo mode is off. */
export function useDemoApi(): DemoApi | null {
  return useBundle().demo;
}
